// src/pages/pmstudio/components/IMSectionsSidebar.jsx

import React, { useEffect, useState } from "react";
import RichTextEditor from "./RichTextEditor.jsx";
import { API_BASE, getCsrfToken } from "../../../services/server";

/* ======================================================================= */
/* INLINE API (no pmstudioApi.js import)                                   */
/* ======================================================================= */

async function apiFetch(path, { method = "GET", body } = {}) {
  const csrf = getCsrfToken();
  const url = `${API_BASE}${path}`;

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("text/html")) {
    const text = await res.text().catch(() => "");
    throw new Error(`Expected JSON but got HTML (${res.status}). ${text.slice(0, 200)}`);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Replace this path to match your backend.
 * Expected: update IM sections/titles and/or active source per section.
 */
async function saveIMSections(orgnr, payload) {
  return apiFetch(`/im-studio/company/${encodeURIComponent(orgnr)}/sections/`, {
    method: "POST",
    body: payload,
  });
}

/* ======================================================================= */

const IM_SECTIONS = [
  { id: "summary", label: "1. Bolagsöversikt" },
  { id: "owners", label: "2. Ägare & bakgrund" },
  { id: "market", label: "3. Marknad & kunder" },
  { id: "offering", label: "4. Erbjudande & tjänster" },
  { id: "business_model", label: "5. Affärsmodell & intäkter" },
  { id: "organisation", label: "6. Organisation & personal" },
  { id: "financials", label: "7. Finansiell utveckling" },
  { id: "valuation", label: "8. Värdering" },
  { id: "risks", label: "9. Nyckelrisker" },
  { id: "uplift", label: "10. Value uplift & möjligheter" },
  { id: "process", label: "11. Process & nästa steg" },
];

/**
 * Props:
 *  - slug
 *  - pm: {
 *      sections, titles,
 *      sections_generated?, titles_generated?,
 *      sections_user?, titles_user?,
 *      active_sources?,
 *    }
 *  - onSectionChange(id, body, title)
 *  - onBeginDragSection(id)
 *  - onEndDrag()
 */
export default function IMSectionsSidebar({
  slug,
  pm,
  onSectionChange,
  onBeginDragSection,
  onEndDrag,
}) {
  const [openId, setOpenId] = useState("summary");

  const [titlesGenerated, setTitlesGenerated] = useState({});
  const [textsGenerated, setTextsGenerated] = useState({});
  const [titlesUser, setTitlesUser] = useState({});
  const [textsUser, setTextsUser] = useState({});

  const [activeSourceById, setActiveSourceById] = useState({});

  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [err, setErr] = useState("");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!pm || initialized) return;

    const effectiveSections = pm.sections && typeof pm.sections === "object" ? pm.sections : {};
    const effectiveTitles = pm.titles && typeof pm.titles === "object" ? pm.titles : {};

    const sectionsGenerated =
      pm.sections_generated && typeof pm.sections_generated === "object" ? pm.sections_generated : {};
    const titlesGeneratedPm =
      pm.titles_generated && typeof pm.titles_generated === "object" ? pm.titles_generated : {};

    const sectionsUserPm = pm.sections_user && typeof pm.sections_user === "object" ? pm.sections_user : {};
    const titlesUserPm = pm.titles_user && typeof pm.titles_user === "object" ? pm.titles_user : {};

    const activeSourcesPm = pm.active_sources && typeof pm.active_sources === "object" ? pm.active_sources : {};

    const nextTitlesGenerated = {};
    const nextTextsGenerated = {};
    const nextTitlesUser = {};
    const nextTextsUser = {};
    const nextActive = {};
    const nextSaved = new Set();

    IM_SECTIONS.forEach((s) => {
      const id = s.id;

      const effBody = typeof effectiveSections[id] === "string" ? effectiveSections[id] : "";
      const effTitle = typeof effectiveTitles[id] === "string" ? effectiveTitles[id] : s.label;

      const genBody = typeof sectionsGenerated[id] === "string" ? sectionsGenerated[id] : effBody;
      const genTitle = typeof titlesGeneratedPm[id] === "string" ? titlesGeneratedPm[id] : effTitle;

      const userBody = typeof sectionsUserPm[id] === "string" ? sectionsUserPm[id] : "";
      const userTitle = typeof titlesUserPm[id] === "string" ? titlesUserPm[id] : "";

      const hasUser = !!(userBody && userBody.trim().length > 0);
      const src = activeSourcesPm[id] || (hasUser ? "user" : "generated");

      nextTitlesGenerated[id] = genTitle;
      nextTextsGenerated[id] = genBody;
      nextTitlesUser[id] = userTitle;
      nextTextsUser[id] = userBody;
      nextActive[id] = src;

      if (hasUser) nextSaved.add(id);
    });

    setTitlesGenerated(nextTitlesGenerated);
    setTextsGenerated(nextTextsGenerated);
    setTitlesUser(nextTitlesUser);
    setTextsUser(nextTextsUser);
    setActiveSourceById(nextActive);
    setSavedIds(nextSaved);
    setInitialized(true);
  }, [pm, initialized]);

  const getCurrentTitle = (id, fallbackLabel) => {
    const src = activeSourceById[id] || "generated";
    if (src === "user") return titlesUser[id] || titlesGenerated[id] || fallbackLabel;
    return titlesGenerated[id] || fallbackLabel;
  };

  const getCurrentBody = (id) => {
    const src = activeSourceById[id] || "generated";
    if (src === "user") return textsUser[id] || "";
    return textsGenerated[id] || "";
  };

  const hasUserVersion = (id) => {
    const b = textsUser[id] || "";
    return b.trim().length > 0;
  };

  const isUsingGenerated = (id) => (activeSourceById[id] || "generated") === "generated";
  const isUsingUser = (id) => (activeSourceById[id] || "generated") === "user";

  const handleSaveUserVersion = async (id) => {
    setSavingId(id);
    setErr("");

    const userBody = textsUser[id] || "";
    const userTitle =
      titlesUser[id] ||
      titlesGenerated[id] ||
      IM_SECTIONS.find((s) => s.id === id)?.label ||
      "";

    try {
      await saveIMSections(slug, {
        sections: { [id]: userBody },
        titles: { [id]: userTitle },
        source: "user",
        set_active_source: { [id]: "user" },
      });

      setActiveSourceById((prev) => ({ ...prev, [id]: "user" }));
      setSavedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      if (onSectionChange) onSectionChange(id, userBody, userTitle);
    } catch (e) {
      console.error("IMSectionsSidebar: failed to save section", id, e);
      setErr("Kunde inte spara sektionen. Försök igen om en stund.");
    } finally {
      setSavingId(null);
    }
  };

  const handleActivateUser = async (id) => {
    const label = IM_SECTIONS.find((s) => s.id === id)?.label || "";
    const genBody = textsGenerated[id] || "";
    const genTitle = titlesGenerated[id] || label;

    let userBody = textsUser[id] || "";
    let userTitle = titlesUser[id] || "";

    const userExists = hasUserVersion(id);

    if (!userExists && genBody) {
      userBody = genBody;
      userTitle = genTitle;
      setTextsUser((prev) => ({ ...prev, [id]: userBody }));
      setTitlesUser((prev) => ({ ...prev, [id]: userTitle }));
    }

    setSavingId(id);
    setErr("");

    try {
      await saveIMSections(slug, {
        sections: { [id]: userBody || "" },
        titles: { [id]: userTitle || "" },
        source: "user",
        set_active_source: { [id]: "user" },
      });

      setActiveSourceById((prev) => ({ ...prev, [id]: "user" }));
      setSavedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      if (onSectionChange) onSectionChange(id, userBody, userTitle);
    } catch (e) {
      console.error("IMSectionsSidebar: failed to activate user version", id, e);
      setErr("Kunde inte växla till egen version. Försök igen om en stund.");
    } finally {
      setSavingId(null);
    }
  };

  const handleActivateGenerated = async (id) => {
    const label = IM_SECTIONS.find((s) => s.id === id)?.label || "";
    const genBody = textsGenerated[id] || "";
    const genTitle = titlesGenerated[id] || label;

    setSavingId(id);
    setErr("");

    try {
      await saveIMSections(slug, {
        sections: {},
        titles: {},
        set_active_source: { [id]: "generated" },
      });

      setActiveSourceById((prev) => ({ ...prev, [id]: "generated" }));
      if (onSectionChange) onSectionChange(id, genBody, genTitle);
    } catch (e) {
      console.error("IMSectionsSidebar: failed to activate generated version", id, e);
      setErr("Kunde inte växla till standardtext. Försök igen om en stund.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="pmstudio-right-inner-inner">
      {err && <div className="pmstudio-error">{err}</div>}

      <div className="pmstudio-sections-accordion">
        {IM_SECTIONS.map((s) => {
          const id = s.id;
          const isOpen = openId === id;

          const src = activeSourceById[id] || "generated";
          const currentBody = getCurrentBody(id);
          const currentTitle = getCurrentTitle(id, s.label);

          const filled = !!(currentBody && currentBody.trim().length > 0);
          const hasUser = hasUserVersion(id);
          const isSavedUser = savedIds.has(id);

          let statusClass = "pmstudio-sec-status pmstudio-sec-status--empty";
          let statusLabel = "Tom sektion";

          if (src === "generated" && filled) {
            statusClass = "pmstudio-sec-status pmstudio-sec-status--generated";
            statusLabel = "Standardtext aktiv";
          }

          if (src === "user") {
            if (hasUser && isSavedUser) {
              statusClass = "pmstudio-sec-status pmstudio-sec-status--saved";
              statusLabel = "Egen version (sparad)";
            } else if (hasUser && !isSavedUser) {
              statusClass = "pmstudio-sec-status pmstudio-sec-status--unsaved";
              statusLabel = "Egen version (ej sparad)";
            }
          }

          return (
            <div key={id} className={"pmstudio-sec-item" + (isOpen ? " is-open" : "")}>
              <div
                className="pmstudio-sec-header pmstudio-sec-header--draggable"
                draggable
                onClick={() => setOpenId((cur) => (cur === id ? "" : id))}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  if (onBeginDragSection) onBeginDragSection(id);
                }}
                onDragEnd={() => {
                  if (onEndDrag) onEndDrag();
                }}
              >
                <div className="pmstudio-sec-header-text">
                  <div className="pmstudio-sec-title">{s.label}</div>
                  <div className="pmstudio-sec-subtitle">{currentTitle}</div>
                </div>

                <span className={statusClass} title={statusLabel} aria-label={statusLabel}>
                  ●
                </span>
                <span className="pmstudio-sec-chevron">{isOpen ? "▾" : "▸"}</span>
              </div>

              {isOpen && (
                <div className="pmstudio-sec-body">
                  <div className="pmstudio-sec-source-row">
                    <span className="pmstudio-sec-source-label">Källa:</span>
                    <span className="pmstudio-sec-source-pill">
                      {src === "generated" ? "Standardtext (backend)" : "Egen version"}
                    </span>
                  </div>

                  <label className="pmstudio-sec-label">Rubrik i dokumentet</label>

                  {isUsingGenerated(id) ? (
                    <input className="pmstudio-sec-input" value={titlesGenerated[id] || s.label} readOnly />
                  ) : (
                    <input
                      className="pmstudio-sec-input"
                      value={titlesUser[id] || titlesGenerated[id] || s.label}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTitlesUser((prev) => ({ ...prev, [id]: value }));
                        if (onSectionChange) onSectionChange(id, textsUser[id] || "", value);
                      }}
                    />
                  )}

                  <label className="pmstudio-sec-label">Brödtext</label>

                  {isUsingGenerated(id) ? (
                    <div className="pmstudio-readonly-block">
                      {textsGenerated[id] && textsGenerated[id].trim() ? (
                        <div className="pmstudio-readonly-text">{textsGenerated[id]}</div>
                      ) : (
                        <div className="pmstudio-readonly-placeholder">Ingen standardtext genererad ännu.</div>
                      )}
                    </div>
                  ) : (
                    <RichTextEditor
                      initialText={textsUser[id] || ""}
                      onChangeText={(plain) => {
                        setTextsUser((prev) => ({ ...prev, [id]: plain }));
                        if (onSectionChange) {
                          onSectionChange(
                            id,
                            plain,
                            titlesUser[id] || titlesGenerated[id] || s.label
                          );
                        }
                      }}
                    />
                  )}

                  <div className="pmstudio-sec-footer pmstudio-sec-footer--split">
                    <div className="pmstudio-sec-footer-left">
                      <button
                        type="button"
                        className="pmstudio-sec-save"
                        disabled={savingId === id || !isUsingUser(id)}
                        onClick={() => handleSaveUserVersion(id)}
                      >
                        {savingId === id ? "Sparar…" : "Spara sektion"}
                      </button>

                      <span className="pmstudio-sec-hint">
                        {isUsingUser(id)
                          ? hasUser
                            ? isSavedUser
                              ? "Egen version är sparad."
                              : "Egen version – klicka ”Spara sektion”."
                            : "Skriv din egen text och spara."
                          : filled
                          ? "Standardtext används. Du kan skapa och aktivera en egen version."
                          : "Ingen text ännu – generera eller skriv egen text."}
                      </span>
                    </div>

                    <div className="pmstudio-sec-footer-right">
                      {isUsingGenerated(id) && (
                        <button
                          type="button"
                          className="pmstudio-sec-toggle-btn"
                          disabled={savingId === id}
                          onClick={() => handleActivateUser(id)}
                        >
                          {hasUser ? "Använd min version" : "Skapa & använd min version"}
                        </button>
                      )}

                      {isUsingUser(id) && (
                        <button
                          type="button"
                          className="pmstudio-sec-toggle-btn pmstudio-sec-toggle-btn--ghost"
                          disabled={savingId === id}
                          onClick={() => handleActivateGenerated(id)}
                        >
                          Återgå till standardtext
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}